import type { PatternKind } from '@lms/shared-types'
import { PATTERN_WINDOW_DAYS, windowPeriod } from './pattern-window'

/**
 * Manager-view pattern copy (Rhythm board), per the FR spec "Manager view
 * patterns". Each pattern shows three things, distinct from the employee's
 * home HealthBarCard copy:
 *
 *  - `title`     — static, manager-facing name of the pattern.
 *  - `dataPoint` — the dynamic "personal data point" line: the SAME live number
 *                  the employee sees (from `patternMetrics`, falling back to the
 *                  detector's evidence count), phrased in the third person.
 *  - `nudge`     — static, directed at MANAGER action (not the employee).
 *
 * Titles + nudges are the approved static strings; the data-point sentence is
 * built from the live metric so the board reads e.g. "6 sick days in the last
 * 90 days." rather than a generic rule definition. Window wording comes from
 * `windowPeriod`, so it reads "this year" when the window is clamped at 1 Jan.
 */

export interface ManagerPatternCopy {
  title: string
  nudge: string
  /** Build the third-person data-point sentence from the pattern's live
   *  collapsed metrics (`patternMetrics[kind]`), the detector evidence count
   *  (fallback when a metric key is absent), and the current month name. */
  dataPoint: (
    metrics: Record<string, number>,
    evidenceCount: number,
    monthName: string,
  ) => string
}

/** Whole numbers render bare (6), halves keep one decimal (5.5). */
function n(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

export const MANAGER_PATTERN_COPY: Record<PatternKind, ManagerPatternCopy> = {
  frequent_sick_leave: {
    title: 'Several sick days recently',
    nudge:
      "Might be worth a quiet check-in ok — not to make it a big thing, just to see how they're doing.",
    dataPoint: (m, c) =>
      `${n(m.sick_days ?? c)} sick days ${windowPeriod(PATTERN_WINDOW_DAYS.frequent_sick_leave ?? 90)}.`,
  },
  poor_planning_emergency: {
    title: 'Repeated short-notice leave',
    nudge:
      'Worth a chat — if it keeps happening, help them plan ahead lah before it affects the team.',
    dataPoint: (m, c) =>
      `${n(m.emergency_count ?? c)} emergency leave requests this year.`,
  },
  emergency_approaching_limit: {
    title: 'Emergency leave adding up',
    nudge:
      'Heads up — a gentle word now can help before it builds into a bigger pattern.',
    dataPoint: (m, c) => `${n(m.emergency_count ?? c)} emergency leaves this year.`,
  },
  unpaid_leave_taken: {
    title: 'Unpaid leave taken',
    nudge:
      "Payroll deduction applied, HR notified. Keep an eye on their balance — don't let it happen again without a plan.",
    dataPoint: (m, c) =>
      `${n(m.unpaid_days ?? c)} days of unpaid leave this year.`,
  },
  frequent_half_days: {
    title: 'Frequent half-days',
    nudge:
      "Happening quite regularly — worth a casual conversation to understand if there's something going on lah.",
    dataPoint: (_m, c) => `${n(c)} half-days in the last 30 days.`,
  },
  long_weekend_pattern: {
    title: 'Long-weekend pattern',
    nudge:
      "Consistent pattern — good to factor in lah when you're planning Monday/Friday coverage for the team.",
    dataPoint: (m, c) =>
      `${n(m.mon_fri_days ?? c)} Monday/Friday leaves ${windowPeriod(PATTERN_WINDOW_DAYS.long_weekend_pattern ?? 90)}.`,
  },
  high_early_utilisation: {
    title: 'High leave usage early in the year',
    nudge:
      'Not many annual days left for the rest of the year — plan H2 coverage accordingly lah.',
    dataPoint: (m, _c, monthName) =>
      m.used_days !== undefined && m.entitlement !== undefined
        ? `${n(m.used_days)} of ${n(m.entitlement)} annual days used by ${monthName}.`
        : 'High share of annual leave used early in the year.',
  },
  no_leave_in_window: {
    title: 'No recent leave',
    nudge:
      "No leave in a while — a nudge to book some rest helps avoid a build-up that's harder to plan around later.",
    dataPoint: () => 'No approved leave in the last 90 days.',
  },
}

/** Live month name (e.g. "July") for the high-early-utilisation data point. */
export function currentMonthName(): string {
  return new Date().toLocaleString('en', { month: 'long' })
}
